import React, { useState, useEffect } from 'react';
import { Tag, Trash2, Plus, Copy, Check, Clock } from 'lucide-react';
import { db, useAuth } from '../../services/firebase';
import { collection, onSnapshot, doc, deleteDoc, addDoc } from 'firebase/firestore';

interface RedeemCode {
    id: string;
    code: string;
    rewardType: 'vip_days' | 'lupas' | 'unlock_case' | 'keys';
    rewardValue: number | string;
    createdAt: Date;
    createdBy: string;
    usedBy?: string;
    usedAt?: Date;
    status: 'active' | 'used' | 'revoked';
}

const CodeManager: React.FC = () => {
    const { user } = useAuth();
    const [codes, setCodes] = useState<RedeemCode[]>([]);
    const [cases, setCases] = useState<{ id: string, title: string }[]>([]);
    const [loading, setLoading] = useState(true);

    const [rewardType, setRewardType] = useState<'vip_days' | 'lupas' | 'unlock_case' | 'keys'>('vip_days');
    const [rewardValue, setRewardValue] = useState<number | string>(7);
    const [newQuantity, setNewQuantity] = useState<number>(1);
    const [customPrefix, setCustomPrefix] = useState<string>('VIP');
    const [copiedId, setCopiedId] = useState<string | null>(null);

    useEffect(() => {
        if (!db) return;

        const unsubscribeCodes = onSnapshot(collection(db, 'redeem_codes'), (snapshot) => {
            const codeList = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    createdAt: data.createdAt ? new Date(data.createdAt.seconds * 1000) : new Date(),
                    usedAt: data.usedAt ? new Date(data.usedAt.seconds * 1000) : undefined,
                } as RedeemCode;
            });
            codeList.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
            setCodes(codeList);
            setLoading(false);
        });

        const unsubscribeCases = onSnapshot(collection(db, 'cases'), (snapshot) => {
            const caseList = snapshot.docs.map(doc => ({ id: doc.id, title: doc.data().title }));
            setCases(caseList);
        });

        return () => { unsubscribeCodes(); unsubscribeCases(); };
    }, []);

    const generateRandomString = (length: number) => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let result = '';
        for (let i = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    };

    const handleCreateCodes = async () => {
        if (!db || !user) return;

        try {
            for (let i = 0; i < newQuantity; i++) {
                const prefix = customPrefix ? `${customPrefix.toUpperCase()}-` : '';
                const codeString = `${prefix}${generateRandomString(4)}-${generateRandomString(4)}`;

                await addDoc(collection(db, 'redeem_codes'), {
                    code: codeString,
                    rewardType: rewardType,
                    rewardValue: rewardValue,
                    createdAt: new Date(),
                    createdBy: user.uid,
                    status: 'active',
                    usedBy: null,
                    usedAt: null
                });
            }
        } catch (error) {
            console.error("Erro ao gerar convites:", error);
            alert("Falha ao gerar códigos.");
        }
    };

    const handleDelete = async (id: string, status: string) => {
        if (!db) return;
        if (status === 'used') {
            if (!window.confirm("Este código já foi usado. Tem certeza que deseja excluí-lo do histórico?")) return;
        } else {
            if (!window.confirm("Tem certeza que deseja revogar e excluir este código gerado?")) return;
        }

        try {
            await deleteDoc(doc(db, 'redeem_codes', id));
        } catch (error) {
            console.error("Erro ao deletar código:", error);
        }
    };

    const handleCopy = (code: string, id: string) => {
        navigator.clipboard.writeText(code);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Carregando códigos...</div>;

    const activeCodes = codes.filter(c => c.status === 'active');
    const usedCodes = codes.filter(c => c.status === 'used');

    return (
        <div className="animate-in fade-in duration-500 flex flex-col h-full">
            <div className="flex justify-between items-center mb-6 shrink-0">
                <h1 className="text-3xl font-bold text-gray-800">Códigos VIP</h1>
                <div className="text-sm text-gray-500">Ativos: <span className="font-bold text-green-600">{activeCodes.length}</span> / Utilizados: <span className="font-bold text-gray-900">{usedCodes.length}</span></div>
            </div>

            {/* Gerador Box */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6 flex flex-col md:flex-row gap-6 items-end shrink-0">
                <div className="w-full md:w-auto">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Prefixo (Opcional)</label>
                    <input
                        type="text"
                        value={customPrefix}
                        onChange={e => setCustomPrefix(e.target.value)}
                        placeholder="Ex: PROMO"
                        className="w-full border border-gray-300 rounded px-4 py-2 focus:ring-2 focus:ring-amber-500 outline-none uppercase font-mono"
                        maxLength={10}
                    />
                </div>
                <div className="w-full md:w-auto">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Recompensa</label>
                    <div className="flex gap-2">
                        <select
                            value={rewardType}
                            onChange={e => {
                                const type = e.target.value as any;
                                setRewardType(type);
                                if (type === 'vip_days') setRewardValue(7);
                                else if (type === 'lupas') setRewardValue(10);
                                else if (type === 'keys') setRewardValue(1);
                                else if (type === 'unlock_case') setRewardValue(cases.length > 0 ? cases[0].id : '');
                            }}
                            className="w-1/2 md:w-auto border border-gray-300 bg-white rounded px-4 py-2 focus:ring-2 focus:ring-amber-500 outline-none"
                        >
                            <option value="vip_days">Assinatura VIP</option>
                            <option value="lupas">Lupas (Energia)</option>
                            <option value="keys">Chaves de Arquivo</option>
                            <option value="unlock_case">Desbloquear Arquivo</option>
                        </select>
                        <select
                            value={rewardValue as any}
                            onChange={e => setRewardValue(rewardType === 'unlock_case' ? e.target.value : Number(e.target.value))}
                            className="w-1/2 md:w-auto max-w-[200px] text-ellipsis border border-gray-300 bg-white rounded px-4 py-2 focus:ring-2 focus:ring-amber-500 outline-none"
                        >
                            {rewardType === 'vip_days' ? (
                                <>
                                    <option value={1}>1 Dia VIP</option>
                                    <option value={3}>3 Dias VIP</option>
                                    <option value={7}>7 Dias VIP</option>
                                    <option value={15}>15 Dias VIP</option>
                                    <option value={30}>1 Mês VIP</option>
                                </>
                            ) : rewardType === 'lupas' ? (
                                <>
                                    <option value={5}>5 Lupas</option>
                                    <option value={10}>10 Lupas</option>
                                    <option value={30}>30 Lupas</option>
                                    <option value={50}>50 Lupas</option>
                                </>
                            ) : rewardType === 'keys' ? (
                                <>
                                    <option value={1}>1 Chave</option>
                                    <option value={3}>3 Chaves</option>
                                    <option value={5}>5 Chaves</option>
                                    <option value={10}>10 Chaves</option>
                                </>
                            ) : (
                                cases.length > 0 ? (
                                    cases.map(c => <option key={c.id} value={c.id}>{c.title}</option>)
                                ) : (
                                    <option value="" disabled>Nenhum caso cadastrado</option>
                                )
                            )}
                        </select>
                    </div>
                </div>
                <div className="w-full md:w-auto">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Quantidade</label>
                    <select
                        value={newQuantity}
                        onChange={e => setNewQuantity(Number(e.target.value))}
                        className="w-full border border-gray-300 bg-white rounded px-4 py-2 focus:ring-2 focus:ring-amber-500 outline-none"
                    >
                        <option value={1}>Gerar 1</option>
                        <option value={5}>Gerar 5</option>
                        <option value={10}>Gerar 10</option>
                        <option value={50}>Gerar 50</option>
                    </select>
                </div>
                <button
                    onClick={handleCreateCodes}
                    className="w-full md:w-auto bg-amber-600 hover:bg-amber-700 text-white font-bold py-2 px-6 rounded flex items-center justify-center gap-2 transition-colors shadow"
                >
                    <Plus size={18} /> GERAR CÓDIGOS
                </button>
            </div>

            {/* Tabela de Códigos */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col flex-1 min-h-0">
                <div className="overflow-auto flex-1">
                    <table className="w-full text-left relative">
                        <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                            <tr>
                                <th className="p-4 text-xs font-bold text-gray-500 uppercase whitespace-nowrap">Código</th>
                                <th className="p-4 text-xs font-bold text-gray-500 uppercase whitespace-nowrap">Concede</th>
                                <th className="p-4 text-xs font-bold text-gray-500 uppercase whitespace-nowrap">Status</th>
                                <th className="p-4 text-xs font-bold text-gray-500 uppercase whitespace-nowrap">Criado Em</th>
                                <th className="p-4 text-xs font-bold text-gray-500 uppercase text-right whitespace-nowrap">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {codes.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="p-8 text-center text-gray-500">Nenhum código gerado ainda.</td>
                                </tr>
                            ) : codes.map(c => (
                                <tr key={c.id} className={`hover:bg-gray-50 transition-colors ${c.status === 'used' ? 'opacity-50 grayscale' : ''}`}>
                                    <td className="p-4 w-[30%]">
                                        <div className="flex items-center gap-2">
                                            <Tag size={16} className={c.status === 'active' ? 'text-amber-500' : 'text-gray-400'} />
                                            <span className="font-mono font-bold text-lg text-gray-800 tracking-wider bg-gray-100 px-2 py-1 rounded border border-gray-200">{c.code}</span>
                                        </div>
                                    </td>
                                    <td className="p-4 w-[20%]">
                                        <div className={`flex items-center gap-1 font-bold ${c.rewardType === 'lupas' ? 'text-blue-600' : c.rewardType === 'unlock_case' ? 'text-purple-600' : c.rewardType === 'keys' ? 'text-amber-500' : 'text-amber-700'}`}>
                                            {c.rewardType === 'vip_days' ? (
                                                <><Clock size={16} /> {c.rewardValue} Dias VIP</>
                                            ) : c.rewardType === 'lupas' ? (
                                                <>🔍 +{c.rewardValue} Lupas</>
                                            ) : c.rewardType === 'keys' ? (
                                                <>🔑 +{c.rewardValue} Chaves</>
                                            ) : (
                                                <><Tag size={14} /> Novo Arquivo</>
                                            )}
                                        </div>
                                        {c.rewardType === 'unlock_case' && (
                                            <div className="text-[10px] text-gray-500 max-w-[150px] truncate font-mono mt-1" title={cases.find(x => x.id === c.rewardValue)?.title || String(c.rewardValue)}>
                                                {cases.find(x => x.id === c.rewardValue)?.title || String(c.rewardValue).substring(0, 8) + '...'}
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-4 w-[25%]">
                                        {c.status === 'active' ? (
                                            <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-green-100 text-green-700 inline-flex items-center gap-1">
                                                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div> Válido
                                            </span>
                                        ) : (
                                            <div className="flex flex-col">
                                                <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-gray-100 text-gray-600 inline-block w-max">
                                                    Resgatado
                                                </span>
                                                <span className="text-[10px] text-gray-500 mt-1 font-mono break-all" title={c.usedBy || ''}>Por: {c.usedBy?.substring(0, 10)}... em {c.usedAt?.toLocaleDateString()}</span>
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-4 w-[15%] text-sm text-gray-500 font-mono">
                                        {c.createdAt.toLocaleDateString()}
                                    </td>
                                    <td className="p-4 text-right w-[10%]">
                                        <div className="flex justify-end gap-2">
                                            {c.status === 'active' && (
                                                <button
                                                    onClick={() => handleCopy(c.code, c.id)}
                                                    className="p-2 rounded hover:bg-gray-200 text-gray-500 transition-colors"
                                                    title="Copiar Código"
                                                >
                                                    {copiedId === c.id ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleDelete(c.id, c.status)}
                                                className="p-2 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                                                title={c.status === 'active' ? "Revogar e Excluir" : "Limpar Histórico"}
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CodeManager;
